import { IonIcon, useIonRouter } from '@ionic/react';
import { chevronForwardOutline } from 'ionicons/icons';
import type { PublicProfileCardFieldsFragment } from '../../../../types/graphql-generated';
import '../../components/ListCard.css';
import {
  LIST_CARD_SUBTITLE_CLASSES,
  LIST_CARD_TITLE_CLASSES,
} from '../../components/listCardStyles';
import { profileNameLines } from '../publicProfileName';
import { CAREER_ROLE_LABELS } from '../careerRoles';
import type { CareerEntryItemEntry } from './CareerEntryItem';
import { ProfileAvatarImage } from './ProfileAvatarImage';

interface PublicProfileCardProps {
  profile: PublicProfileCardFieldsFragment;
  /** In listings, tapping the card opens the user's public profile. */
  navigable?: boolean;
  /** Route prefix for public profiles in the current tab's navigation stack. */
  profilePathPrefix?: string;
  /** Career entries responsible for this profile appearing in an overlap listing. */
  matchingCareerEntries?: CareerEntryItemEntry[];
}

const formatFullCareerDate = (date: string) => {
  const parts = new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(new Date(`${date}T00:00:00Z`));
  const value = (type: 'day' | 'month' | 'year') =>
    parts.find((part) => part.type === type)?.value;

  return `${value('day')} ${value('month')} ${value('year')}`;
};

// Identity block of another user (picture, name and career status), laid out
// like the header of the ProfileTab but read-only and without the email.
export const PublicProfileCard = ({
  profile,
  navigable = false,
  profilePathPrefix = '/app/main/perfil/usuario',
  matchingCareerEntries = [],
}: PublicProfileCardProps) => {
  const ionRouter = useIonRouter();
  const { primary: name, secondary: secondaryName } = profileNameLines(profile, 'Usuario');

  const content = (
    <>
      <div className="flex items-center gap-4">
        <ProfileAvatarImage
          picture={profile.picture}
          updatedAt={profile.updatedAt}
          alt={`Foto de ${name}`}
          className="h-[76px] w-[76px] shrink-0 border-2 border-white shadow-md"
        />
        <div className="min-w-0 flex-1">
          <h2 className={`m-0 truncate ${LIST_CARD_TITLE_CLASSES}`}>
            {name}
          </h2>
          {secondaryName && (
            <p className={`m-0 truncate ${LIST_CARD_SUBTITLE_CLASSES}`}>{secondaryName}</p>
          )}
          {profile.careerStatus && (
            <p className={`m-0 truncate ${LIST_CARD_SUBTITLE_CLASSES}`}>{profile.careerStatus}</p>
          )}
          {matchingCareerEntries.map((entry) => (
            <p key={entry.id} className="m-0 mt-2 text-sm leading-5 text-slate-600">
              <span className="font-semibold text-slate-700">{entry.careerOrganization.name}:</span>{' '}
              {formatFullCareerDate(entry.startDate)} –{' '}
              {entry.endDate ? formatFullCareerDate(entry.endDate) : 'Actualidad'}.{' '}
              {CAREER_ROLE_LABELS[entry.role]}.
              {entry.details && <> {entry.details}</>}
            </p>
          ))}
        </div>
        {navigable && (
          <IonIcon icon={chevronForwardOutline} className="shrink-0 text-xl text-slate-400" />
        )}
      </div>
    </>
  );

  if (!navigable) {
    return (
      <div
        className="fc-list-card border border-[#e8ebf1] bg-white p-4"
        data-testid="public-profile-card"
      >
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      className="fc-list-card mb-3 block w-full border border-[#e8ebf1] bg-white text-left"
      onClick={() => ionRouter.push(`${profilePathPrefix}/${profile.id}`)}
      data-testid="public-profile-card"
    >
      {content}
    </button>
  );
};
